/**
 * Premium Cinematic Animations Logic
 * Mobile-first, GPU-accelerated, and performance-optimized.
 */

(function () {
    'use strict';

    // Configuration
    const CONFIG = {
        parallaxRatioDesktop: 0.08,
        parallaxRatioMobile: 0.04,
        tiltMaxRotation: 5,
        tiltEasing: 0.1,
        revealThreshold: 0.15,
        isMobile: window.innerWidth < 768,
        lowPowerMode: false
    };

    /**
     * Intersection Observer for Reveal Animations
     */
    const initReveals = () => {
        const observerOptions = {
            threshold: 0.1, // Lower threshold for more responsive reveal
            rootMargin: '0px 0px -50px 0px'
        };

        const revealObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');

                    if (entry.target instanceof HTMLElement && entry.target.classList.contains('premium-reveal-group')) {
                        const children = entry.target.querySelectorAll(':scope > *');
                        children.forEach((child, index) => {
                            if (child instanceof HTMLElement) {
                                setTimeout(() => {
                                    child.style.opacity = '1';
                                    child.style.transform = 'translateY(0) scale(1)';
                                    child.style.transition = `opacity 0.8s var(--premium-ease) ${index * 0.1}s, transform 0.8s var(--premium-ease) ${index * 0.1}s`;
                                }, 50);
                            }
                        });
                    }
                    revealObserver.unobserve(entry.target);
                    entry.target.classList.add('premium-revealed');
                }
            });
        }, observerOptions);

        const revealTargets = document.querySelectorAll('.premium-reveal, .section-content-wrapper, .product-grid__item, .collection-card');
        revealTargets.forEach(target => {
            if (target instanceof HTMLElement) {
                // Ensure starting state
                if (!target.classList.contains('is-visible') && !target.classList.contains('premium-revealed')) {
                    if (!target.classList.contains('premium-reveal')) {
                        target.classList.add('premium-reveal', 'premium-reveal--up');
                    }
                    revealObserver.observe(target);
                } else {
                    // Already visible or revealed, make sure it stays sharp
                    target.style.filter = 'none';
                    target.style.opacity = '1';
                }
            }
        });
    };

    /**
     * Scroll Progress Bar
     */
    const initProgressBar = () => {
        const bar = document.createElement('div');
        bar.className = 'scroll-progress-bar';
        const inner = document.createElement('div');
        inner.className = 'scroll-progress-inner';
        bar.appendChild(inner);
        document.body.appendChild(bar);

        window.addEventListener('scroll', () => {
            const h = document.documentElement;
            const b = document.body;
            const st = 'scrollTop';
            const sh = 'scrollHeight';
            const percent = (h[st] || b[st]) / ((h[sh] || b[sh]) - h.clientHeight);
            bar.style.transform = `scaleX(${percent})`;
        }, { passive: true });
    };

    /**
     * Enhanced Scroll-Driven Visuals (Parallax, Scaling, Text)
     */
    const initScrollDrivenVisuals = () => {
        const visuals = document.querySelectorAll('.background-image-container img, .parallax-layer, h1, h2');

        const tick = () => {
            visuals.forEach(el => {
                if (!(el instanceof HTMLElement)) return;
                const parent = el.parentElement;
                if (!parent) return;

                const rect = parent.getBoundingClientRect();
                const vh = window.innerHeight;

                if (rect.top < vh && rect.bottom > 0) {
                    const centerOffset = (rect.top + rect.height / 2) / vh;
                    const progress = Math.max(0, Math.min(1, 1 - centerOffset));

                    if (el.tagName === 'IMG' || el.classList.contains('parallax-layer')) {
                        const move = (progress - 0.5) * vh * (CONFIG.isMobile ? 0.05 : 0.1);
                        const scale = 1.05 + (Math.abs(progress - 0.5) * 0.1);
                        el.style.transform = `translate3d(0, ${move}px, 0) scale(${scale})`;
                    } else if (el.tagName === 'H1' || el.tagName === 'H2') {
                        const move = (progress - 0.5) * 40;
                        el.style.transform = `translate3d(0, ${move}px, 0)`;
                        el.style.letterSpacing = `${(progress - 0.5) * 2}px`;
                    }
                    el.style.willChange = 'transform';
                }
            });
            requestAnimationFrame(tick);
        };

        if (!CONFIG.lowPowerMode) {
            requestAnimationFrame(tick);
        }
    };

    /**
     * 3D Card Tilt Interaction (Desktop only)
     */
    const initCardTilt = () => {
        if (CONFIG.isMobile) return;

        const cards = document.querySelectorAll('.product-card, .collection-card');

        cards.forEach(card => {
            if (!(card instanceof HTMLElement)) return;
            card.classList.add('premium-card-3d');
            const inner = card.querySelector('.product-card__content, .collection-card__image-wrapper') || card;
            if (!(inner instanceof HTMLElement)) return;

            card.addEventListener('mousemove', (e) => {
                if (!(e instanceof MouseEvent)) return;
                const rect = card.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;

                const centerX = rect.width / 2;
                const centerY = rect.height / 2;

                const rotateX = ((y - centerY) / centerY) * -CONFIG.tiltMaxRotation;
                const rotateY = ((x - centerX) / centerX) * CONFIG.tiltMaxRotation;

                inner.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
                inner.style.transition = 'transform 0.1s ease-out';
                inner.style.zIndex = '10';
            });

            card.addEventListener('mouseleave', () => {
                inner.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
                inner.style.transition = 'transform 0.5s ease-in-out';
                setTimeout(() => { inner.style.zIndex = ''; }, 500);
            });
        });
    };

    /**
     * Cinematic Blur & Focus Effects
     */
    const initScrollDepth = () => {
        const sections = Array.from(document.querySelectorAll('.shopify-section:not(.header-section)')).filter(s => {
            return !s.querySelector('.product-information, .main-product');
        });

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const target = entry.target;
                if (!(target instanceof HTMLElement)) return;

                // Calculate how much of the viewport this section occupies
                const visibleHeight = entry.intersectionRect.height;
                const viewportHeight = window.innerHeight;
                const viewportCoverage = visibleHeight / viewportHeight;

                // A section should be sharp if:
                // 1. It occupies a significant portion of the viewport (>30%)
                // 2. OR it is mostly visible (for small sections, ratio > 0.5)
                // 3. OR it is near the center of the viewport
                const shouldBeSharp = viewportCoverage > 0.3 || entry.intersectionRatio > 0.5;

                if (entry.isIntersecting && shouldBeSharp) {
                    target.style.filter = 'blur(0px)';
                    target.style.opacity = '1';
                } else if (entry.isIntersecting) {
                    // Peek state: slightly blurred at edges
                    target.style.filter = `blur(${CONFIG.isMobile ? '2px' : '4px'})`;
                    target.style.opacity = '0.85';
                } else {
                    // Off-screen: reset to sharp for next entrance
                    target.style.filter = 'blur(0px)';
                    target.style.opacity = '1';
                }
                target.style.transition = 'filter 0.8s ease, opacity 0.8s ease';
            });
        }, { threshold: [0, 0.1, 0.3, 0.5, 0.8] });

        sections.forEach(s => observer.observe(s));
    };

    /**
     * Bootstrap
     */
    const init = () => {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
            return;
        }

        initReveals();
        initProgressBar();
        initScrollDrivenVisuals();
        initCardTilt();
        initScrollDepth();

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            CONFIG.lowPowerMode = true;
        }
    };

    init();

})();
